import React, { useState, useCallback, useEffect } from 'react';
import { apiClient } from '../api/client';
import {
  PRIMARY,
  TEXT_DARK,
  TEXT_MUTED,
  BORDER_LIGHT,
  BG_PAGE,
  BG_CARD,
  BG_SURFACE,
} from '../components/legacy-ball/table-management/designTokens';

// ── Mobile Detection Hook ─────────────────────────────────────

function useIsMobile(breakpoint = 768): boolean {
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth < breakpoint : false,
  );
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < breakpoint);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [breakpoint]);
  return isMobile;
}

// ── Type Definitions ──────────────────────────────────────────

export type ObservabilityCategory = 'system-health' | 'login-audit' | 'data-audit' | 'error-reports' | 'data-integrity' | 'guest-accounts' | 'registration-stats' | 'data-downloads';

export interface CategoryState<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
  lastFetched: Date | null;
}

export interface HealthResponse {
  status: 'ok' | 'degraded' | 'error';
  timestamp: string;
  build: { commit: string; deployedAt: string };
  services: { database: 'connected' | 'disconnected'; redis: 'connected' | 'disconnected'; openai?: 'connected' | 'disconnected' | 'no_key' };
}

export interface LoginAuditResponse {
  data: Array<{
    id: string;
    identifier: string;
    fullName: string | null;
    outcome: 'success' | 'failure';
    failureReason: string | null;
    userId: string | null;
    userRole: string | null;
    ipAddress: string;
    userAgent: string;
    city: string | null;
    country: string | null;
    countryCode: string | null;
    timestamp: string;
  }>;
  total: number;
  page: number;
  pageSize: number;
}

export interface AuditSummaryResponse {
  totalToday: number;
  byAction: {
    CREATE: number;
    UPDATE: number;
    DELETE: number;
    BULK_CREATE: number;
    BULK_UPDATE: number;
    BULK_DELETE: number;
  };
  topModels: Array<{ modelName: string; count: number }>;
  recentEntries: Array<{
    id: string;
    modelName: string;
    action: string;
    actorName: string | null;
    timestamp: string;
  }>;
}

export interface ErrorReportsResponse {
  data: Array<{
    id: string;
    screenSlug: string;
    screenAlias: string | null;
    reporterName: string;
    description: string;
    severity: string;
    status: string;
    createdAt: string;
  }>;
  total: number;
  page: number;
  totalPages: number;
}

export interface UnlinkedTicketsResponse {
  total: number;
  tickets: Array<{
    ticketNumber: number;
    formattedTicket: string;
    guestName: string | null;
    tierName: string;
    status: string;
    mealOption: string | null;
    orderRef: string | null;
    purchaserName: string | null;
  }>;
}

export interface GuestAccountsResponse {
  totalUsers: number;
  syntheticAccounts: number;
  mustChangePassword: number;
  disabledAccounts: number;
  byRole: { Admin: number; Organizer: number; Delegate: number; Attendee: number };
}

export interface RegistrationStatsResponse {
  totalGuestRecords: number;
  totalRegistrations: number;
  checkedIn: number;
  unseatedGuests: number;
  guestsWithoutMeal: number;
  byChapter: Array<{ chapter: string; count: number; noMeal: number }>;
}

export interface DashboardState {
  activeCategory: ObservabilityCategory | null;
  health: CategoryState<HealthResponse>;
  loginAudit: CategoryState<LoginAuditResponse>;
  dataAudit: CategoryState<AuditSummaryResponse>;
  errorReports: CategoryState<ErrorReportsResponse>;
  dataIntegrity: CategoryState<UnlinkedTicketsResponse>;
  guestAccounts: CategoryState<GuestAccountsResponse>;
  registrationStats: CategoryState<RegistrationStatsResponse>;
  lastUpdated: Date | null;
}

// ── Initial State ─────────────────────────────────────────────

function createInitialCategoryState<T>(): CategoryState<T> {
  return { data: null, loading: false, error: null, lastFetched: null };
}

const initialState: DashboardState = {
  activeCategory: null,
  health: createInitialCategoryState<HealthResponse>(),
  loginAudit: createInitialCategoryState<LoginAuditResponse>(),
  dataAudit: createInitialCategoryState<AuditSummaryResponse>(),
  errorReports: createInitialCategoryState<ErrorReportsResponse>(),
  dataIntegrity: createInitialCategoryState<UnlinkedTicketsResponse>(),
  guestAccounts: createInitialCategoryState<GuestAccountsResponse>(),
  registrationStats: createInitialCategoryState<RegistrationStatsResponse>(),
  lastUpdated: null,
};

// ── CategoryCard Sub-Component ────────────────────────────────

interface CategoryCardProps {
  icon: string;
  name: string;
  metrics: Array<{ label: string; value: string | number }>;
  isActive: boolean;
  isLoading: boolean;
  hasError: boolean;
  onClick: () => void;
  onRetry: () => void;
}

const categoryCardStyles: Record<string, React.CSSProperties> = {
  card: {
    backgroundColor: BG_CARD,
    borderRadius: '8px',
    border: `1px solid ${BORDER_LIGHT}`,
    padding: '1rem',
    cursor: 'pointer',
    transition: 'border-color 0.15s, box-shadow 0.15s',
    minWidth: '140px',
    flexShrink: 0,
  },
  cardActive: {
    backgroundColor: BG_CARD,
    borderRadius: '8px',
    border: `2px solid ${PRIMARY}`,
    padding: 'calc(1rem - 1px)',
    cursor: 'pointer',
    transition: 'border-color 0.15s, box-shadow 0.15s',
    boxShadow: `0 0 0 1px ${PRIMARY}20`,
    minWidth: '140px',
    flexShrink: 0,
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '0.75rem',
  },
  cardIcon: {
    fontSize: '1.5rem',
  },
  cardName: {
    fontSize: '0.95rem',
    fontWeight: 600,
    color: TEXT_DARK,
    margin: 0,
  },
  metricRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '3px 0',
  },
  metricLabel: {
    fontSize: '0.8rem',
    color: TEXT_MUTED,
  },
  metricValue: {
    fontSize: '0.8rem',
    fontWeight: 600,
    color: TEXT_DARK,
  },
  loadingIndicator: {
    fontSize: '0.8rem',
    color: TEXT_MUTED,
    padding: '0.5rem 0',
  },
  errorIndicator: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0.5rem 0',
  },
  errorText: {
    fontSize: '0.8rem',
    color: '#d32f2f',
  },
  retryBtn: {
    fontSize: '0.75rem',
    color: PRIMARY,
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    fontWeight: 600,
    padding: '2px 6px',
    borderRadius: '4px',
  },
};

const CategoryCard: React.FC<CategoryCardProps> = ({
  icon,
  name,
  metrics,
  isActive,
  isLoading,
  hasError,
  onClick,
  onRetry,
}) => {
  return (
    <div
      style={isActive ? categoryCardStyles.cardActive : categoryCardStyles.card}
      onClick={onClick}
      role="button"
      tabIndex={0}
      aria-label={`${name} category`}
      aria-pressed={isActive}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } }}
    >
      <div style={categoryCardStyles.cardHeader}>
        <span style={categoryCardStyles.cardIcon}>{icon}</span>
        <p style={categoryCardStyles.cardName}>{name}</p>
      </div>
      {isLoading && (
        <p style={categoryCardStyles.loadingIndicator}>Loading...</p>
      )}
      {hasError && !isLoading && (
        <div style={categoryCardStyles.errorIndicator}>
          <span style={categoryCardStyles.errorText}>⚠️ Failed to load</span>
          <button
            style={categoryCardStyles.retryBtn}
            onClick={(e) => { e.stopPropagation(); onRetry(); }}
            aria-label={`Retry loading ${name}`}
          >
            Retry
          </button>
        </div>
      )}
      {!isLoading && !hasError && metrics.length > 0 && (
        <div>
          {metrics.slice(0, 3).map((metric) => (
            <div key={metric.label} style={categoryCardStyles.metricRow}>
              <span style={categoryCardStyles.metricLabel}>{metric.label}</span>
              <span style={categoryCardStyles.metricValue}>{metric.value}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ── Detail Panel Sub-Components ────────────────────────────────

const detailStyles: Record<string, React.CSSProperties> = {
  heading: {
    fontSize: '1.25rem',
    fontWeight: 700,
    color: TEXT_DARK,
    margin: '0 0 1.25rem 0',
  },
  section: {
    marginBottom: '1.5rem',
  },
  sectionTitle: {
    fontSize: '0.9rem',
    fontWeight: 600,
    color: TEXT_DARK,
    margin: '0 0 0.5rem 0',
  },
  metricGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
    gap: '10px',
    marginBottom: '1rem',
  },
  metricCard: {
    padding: '10px 12px',
    backgroundColor: BG_PAGE,
    borderRadius: '6px',
    border: `1px solid ${BORDER_LIGHT}`,
  },
  metricLabel: {
    fontSize: '0.75rem',
    color: TEXT_MUTED,
    margin: '0 0 4px 0',
  },
  metricValue: {
    fontSize: '1.1rem',
    fontWeight: 700,
    color: TEXT_DARK,
    margin: 0,
  },
  statusBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '4px 10px',
    borderRadius: '12px',
    fontSize: '0.8rem',
    fontWeight: 600,
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse' as const,
    fontSize: '0.8rem',
  },
  th: {
    textAlign: 'left' as const,
    padding: '8px 10px',
    borderBottom: `2px solid ${BORDER_LIGHT}`,
    color: TEXT_MUTED,
    fontWeight: 600,
    fontSize: '0.75rem',
  },
  td: {
    padding: '7px 10px',
    borderBottom: `1px solid ${BORDER_LIGHT}`,
    color: TEXT_DARK,
  },
  loadingContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '3rem',
    color: TEXT_MUTED,
    fontSize: '0.9rem',
  },
  errorContainer: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    justifyContent: 'center',
    padding: '3rem',
    gap: '12px',
  },
  errorText: {
    color: '#d32f2f',
    fontSize: '0.9rem',
  },
  retryButton: {
    padding: '8px 16px',
    backgroundColor: PRIMARY,
    color: '#ffffff',
    border: 'none',
    borderRadius: '6px',
    fontSize: '0.85rem',
    fontWeight: 600,
    cursor: 'pointer',
  },
  infoRow: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '6px 0',
    borderBottom: `1px solid ${BORDER_LIGHT}`,
  },
  infoLabel: {
    fontSize: '0.8rem',
    color: TEXT_MUTED,
  },
  infoValue: {
    fontSize: '0.8rem',
    fontWeight: 600,
    color: TEXT_DARK,
  },
};

function getStatusColor(status: string): string {
  switch (status) {
    case 'ok':
    case 'success':
    case 'connected':
    case 'resolved':
      return '#16a34a';
    case 'degraded':
    case 'in_progress':
      return '#ca8a04';
    case 'error':
    case 'failure':
    case 'disconnected':
    case 'open':
      return '#dc2626';
    default:
      return TEXT_MUTED;
  }
}

function getStatusBgColor(status: string): string {
  switch (status) {
    case 'ok':
    case 'success':
    case 'connected':
    case 'resolved':
      return '#dcfce7';
    case 'degraded':
    case 'in_progress':
      return '#fef9c3';
    case 'error':
    case 'failure':
    case 'disconnected':
    case 'open':
      return '#fee2e2';
    default:
      return BG_PAGE;
  }
}

// ── SystemHealthDetail ────────────────────────────────────────

interface DetailPanelProps {
  loading: boolean;
  error: string | null;
  onRetry: () => void;
}

const SystemHealthDetail: React.FC<DetailPanelProps & { data: HealthResponse | null }> = ({
  data,
  loading,
  error,
  onRetry,
}) => {
  if (loading) {
    return (
      <div style={detailStyles.loadingContainer}>
        <span>⏳ Loading system health data...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div style={detailStyles.errorContainer}>
        <span style={detailStyles.errorText}>⚠️ {error}</span>
        <button style={detailStyles.retryButton} onClick={onRetry}>Retry</button>
      </div>
    );
  }

  if (!data) return null;

  return (
    <div>
      <h2 style={detailStyles.heading}>🖥️ System Health</h2>

      <div style={detailStyles.section}>
        <p style={detailStyles.sectionTitle}>Overall Status</p>
        <span
          style={{
            ...detailStyles.statusBadge,
            color: getStatusColor(data.status),
            backgroundColor: getStatusBgColor(data.status),
          }}
        >
          ● {data.status.toUpperCase()}
        </span>
      </div>

      <div style={detailStyles.section}>
        <p style={detailStyles.sectionTitle}>Service Connectivity</p>
        <div style={detailStyles.metricGrid}>
          <div style={detailStyles.metricCard}>
            <p style={detailStyles.metricLabel}>Database</p>
            <span
              style={{
                ...detailStyles.statusBadge,
                color: getStatusColor(data.services.database),
                backgroundColor: getStatusBgColor(data.services.database),
              }}
            >
              ● {data.services.database}
            </span>
          </div>
          <div style={detailStyles.metricCard}>
            <p style={detailStyles.metricLabel}>Redis</p>
            <span
              style={{
                ...detailStyles.statusBadge,
                color: getStatusColor(data.services.redis),
                backgroundColor: getStatusBgColor(data.services.redis),
              }}
            >
              ● {data.services.redis}
            </span>
          </div>
          <div style={detailStyles.metricCard}>
            <p style={detailStyles.metricLabel}>OpenAI API</p>
            <span
              style={{
                ...detailStyles.statusBadge,
                color: getStatusColor(data.services.openai || 'no_key'),
                backgroundColor: getStatusBgColor(data.services.openai || 'no_key'),
              }}
            >
              ● {data.services.openai || 'no_key'}
            </span>
          </div>
        </div>
      </div>

      <div style={detailStyles.section}>
        <p style={detailStyles.sectionTitle}>Build & Deployment</p>
        <div style={detailStyles.infoRow}>
          <span style={detailStyles.infoLabel}>Commit Hash</span>
          <span style={{ ...detailStyles.infoValue, fontFamily: 'monospace' }}>
            {data.build.commit || '—'}
          </span>
        </div>
        <div style={detailStyles.infoRow}>
          <span style={detailStyles.infoLabel}>Deployed At</span>
          <span style={detailStyles.infoValue}>
            {data.build.deployedAt ? new Date(data.build.deployedAt).toLocaleString() : '—'}
          </span>
        </div>
        <div style={detailStyles.infoRow}>
          <span style={detailStyles.infoLabel}>Last Checked</span>
          <span style={detailStyles.infoValue}>
            {new Date(data.timestamp).toLocaleString()}
          </span>
        </div>
      </div>
    </div>
  );
};

// ── LoginAuditDetail ──────────────────────────────────────────

const LoginAuditDetail: React.FC<DetailPanelProps & { data: LoginAuditResponse | null }> = ({
  data,
  loading,
  error,
  onRetry,
}) => {
  const [entries, setEntries] = useState<LoginAuditResponse['data']>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalEntries, setTotalEntries] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  const PAGE_SIZE = 20;

  // Sync initial data
  useEffect(() => {
    if (data) {
      setEntries(data.data);
      setCurrentPage(data.page);
      setTotalEntries(data.total);
    }
  }, [data]);

  const handleLoadMore = async () => {
    setLoadingMore(true);
    try {
      const today = new Date();
      const todayStart = today.toISOString().split('T')[0];
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);
      const tomorrowStr = tomorrow.toISOString().split('T')[0];
      const nextPage = currentPage + 1;
      const result = await apiClient<LoginAuditResponse>(
        `/login-audit?startDate=${todayStart}&endDate=${tomorrowStr}&page=${nextPage}&pageSize=${PAGE_SIZE}`,
      );
      setEntries((prev) => [...prev, ...result.data]);
      setCurrentPage(nextPage);
      setTotalEntries(result.total);
    } catch {
      // silently fail
    } finally {
      setLoadingMore(false);
    }
  };

  if (loading) {
    return (
      <div style={detailStyles.loadingContainer}>
        <span>⏳ Loading login audit data...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div style={detailStyles.errorContainer}>
        <span style={detailStyles.errorText}>⚠️ {error}</span>
        <button style={detailStyles.retryButton} onClick={onRetry}>Retry</button>
      </div>
    );
  }

  if (!data) return null;

  const successCount = entries.filter((e) => e.outcome === 'success').length;
  const failureCount = entries.filter((e) => e.outcome === 'failure').length;

  // Breakdown by role
  const roleBreakdown: Record<string, number> = {};
  entries.forEach((e) => {
    const role = e.userRole || 'Unknown';
    roleBreakdown[role] = (roleBreakdown[role] || 0) + 1;
  });

  // Breakdown of failure reasons
  const failureReasons: Record<string, number> = {};
  entries
    .filter((e) => e.outcome === 'failure' && e.failureReason)
    .forEach((e) => {
      const reason = e.failureReason!;
      failureReasons[reason] = (failureReasons[reason] || 0) + 1;
    });

  // Top locations breakdown
  const locationBreakdown: Record<string, number> = {};
  entries.forEach((e) => {
    if (e.city || e.country) {
      const loc = e.city && e.country ? `${e.city}, ${e.countryCode || e.country}` : (e.country || e.city || '');
      locationBreakdown[loc] = (locationBreakdown[loc] || 0) + 1;
    }
  });
  const topLocations = Object.entries(locationBreakdown).sort((a, b) => b[1] - a[1]).slice(0, 6);

  // Mobile vs Desktop ratio
  const deviceStats = { mobile: 0, desktop: 0 };
  entries.forEach((e) => {
    if (!e.userAgent) return;
    if (/iPhone|iPad|Android|Mobile/i.test(e.userAgent)) {
      deviceStats.mobile++;
    } else {
      deviceStats.desktop++;
    }
  });

  // Parse user-agent into a short readable string
  const parseUA = (ua: string): string => {
    if (!ua) return '—';
    // Try to extract browser + OS
    let browser = 'Unknown';
    let os = 'Unknown';
    if (ua.includes('Chrome') && !ua.includes('Edg')) browser = 'Chrome';
    else if (ua.includes('Edg')) browser = 'Edge';
    else if (ua.includes('Firefox')) browser = 'Firefox';
    else if (ua.includes('Safari') && !ua.includes('Chrome')) browser = 'Safari';
    else if (ua.includes('PostmanRuntime')) browser = 'Postman';

    if (ua.includes('iPhone') || ua.includes('iPad')) os = 'iOS';
    else if (ua.includes('Android')) os = 'Android';
    else if (ua.includes('Mac OS X')) os = 'macOS';
    else if (ua.includes('Windows')) os = 'Windows';
    else if (ua.includes('Linux')) os = 'Linux';

    return `${browser} / ${os}`;
  };

  const formatTime = (ts: string): string => {
    const d = new Date(ts);
    return d.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
    });
  };

  const hasMore = entries.length < totalEntries;

  return (
    <div>
      <h2 style={detailStyles.heading}>🔐 Login Audit</h2>

      {/* AI Intelligence Board */}
      {entries.length > 0 && (() => {
        // Hourly activity
        const hourlyAct: number[] = new Array(24).fill(0);
        const hourlyFail: number[] = new Array(24).fill(0);
        entries.forEach((e) => {
          const h = new Date(e.timestamp).getHours();
          hourlyAct[h]++;
          if (e.outcome === 'failure') hourlyFail[h]++;
        });
        const maxH = Math.max(...hourlyAct, 1);

        // Geo
        const geoM: Record<string, number> = {};
        entries.forEach((e) => {
          const loc = e.city && e.countryCode ? `${e.city}, ${e.countryCode}` : (e.country || null);
          if (loc) geoM[loc] = (geoM[loc] || 0) + 1;
        });
        const topG = Object.entries(geoM).sort((a, b) => b[1] - a[1]).slice(0, 5);
        const maxG = topG.length > 0 ? topG[0][1] : 1;

        // Threat score
        const ipFails: Record<string, number> = {};
        entries.filter((e) => e.outcome === 'failure').forEach((e) => { ipFails[e.ipAddress] = (ipFails[e.ipAddress] || 0) + 1; });
        const susIPs = Object.entries(ipFails).filter(([, c]) => c >= 3);
        const failRate = entries.length > 0 ? entries.filter((e) => e.outcome === 'failure').length / entries.length : 0;
        const tScore = Math.min(100, Math.round(failRate * 40 + susIPs.length * 15 + (entries.filter((e) => e.outcome === 'failure').length > 10 ? 20 : 0)));
        const tColor = tScore < 20 ? '#4ade80' : tScore < 50 ? '#fbbf24' : '#ef4444';
        const tLabel = tScore < 20 ? 'LOW' : tScore < 50 ? 'MEDIUM' : 'HIGH';

        // Mobile vs desktop
        const mob = entries.filter((e) => /iPhone|iPad|Android|Mobile/i.test(e.userAgent)).length;

        const brdStyle: React.CSSProperties = { marginBottom: '20px', borderRadius: '14px', background: 'linear-gradient(135deg, #0f2d1f 0%, #1a4a2e 50%, #0d2418 100%)', padding: '20px', color: '#fff' };
        const cardS: React.CSSProperties = { background: 'rgba(255,255,255,0.08)', borderRadius: '10px', padding: '14px', border: '1px solid rgba(255,255,255,0.1)' };
        const cardTitleS: React.CSSProperties = { fontSize: '0.65rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1px', color: 'rgba(255,255,255,0.5)', marginBottom: '10px' };

        return (
          <div style={brdStyle}>
            <div style={{ fontSize: '0.85rem', fontWeight: 700, marginBottom: '14px' }}>🤖 AI Login Intelligence</div>
            {/* Metrics row */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))', gap: '10px', marginBottom: '14px' }}>
              <div style={{ ...cardS, textAlign: 'center' }}>
                <div style={{ fontSize: '1.6rem', fontWeight: 800, color: '#4ade80' }}>{Math.round((1 - failRate) * 100)}%</div>
                <div style={{ fontSize: '0.6rem', color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase' }}>Success Rate</div>
              </div>
              <div style={{ ...cardS, textAlign: 'center' }}>
                <div style={{ fontSize: '1.6rem', fontWeight: 800, color: tColor }}>{tScore}</div>
                <div style={{ fontSize: '0.6rem', color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase' }}>Threat ({tLabel})</div>
              </div>
              <div style={{ ...cardS, textAlign: 'center' }}>
                <div style={{ fontSize: '1.6rem', fontWeight: 800, color: '#60a5fa' }}>{new Set(entries.map((e) => e.ipAddress)).size}</div>
                <div style={{ fontSize: '0.6rem', color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase' }}>Unique IPs</div>
              </div>
              <div style={{ ...cardS, textAlign: 'center' }}>
                <div style={{ fontSize: '1.6rem', fontWeight: 800, color: '#c084fc' }}>📱{mob} 🖥️{entries.length - mob}</div>
                <div style={{ fontSize: '0.6rem', color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase' }}>Mobile / Desktop</div>
              </div>
            </div>
            {/* Charts row */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              {/* Hourly */}
              <div style={cardS}>
                <div style={cardTitleS}>⏰ 24h Activity</div>
                <div style={{ display: 'flex', alignItems: 'flex-end', gap: '1px', height: '50px' }}>
                  {hourlyAct.map((c, h) => (
                    <div key={h} style={{ flex: 1, height: `${Math.max(3, (c / maxH) * 46)}px`, borderRadius: '2px 2px 0 0', background: hourlyFail[h] > 0 ? `linear-gradient(to top, #ef4444 ${(hourlyFail[h] / Math.max(c, 1)) * 100}%, #4ade80 0%)` : c > 0 ? '#4ade80' : 'rgba(255,255,255,0.1)' }} title={`${h}:00 — ${c} (${hourlyFail[h]} fail)`} />
                  ))}
                </div>
              </div>
              {/* Geo */}
              <div style={cardS}>
                <div style={cardTitleS}>🌍 Locations</div>
                {topG.length === 0 ? (
                  <div style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.4)', textAlign: 'center', padding: '10px 0' }}>No geo data yet</div>
                ) : topG.map(([loc, cnt]) => (
                  <div key={loc} style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
                    <span style={{ fontSize: '0.65rem', color: 'rgba(255,255,255,0.7)', minWidth: '70px', textAlign: 'right' }}>{loc}</span>
                    <div style={{ flex: 1, height: '6px', borderRadius: '3px', background: 'rgba(255,255,255,0.1)' }}>
                      <div style={{ height: '100%', width: `${(cnt / maxG) * 100}%`, borderRadius: '3px', background: '#4ade80' }} />
                    </div>
                    <span style={{ fontSize: '0.65rem', fontWeight: 600 }}>{cnt}</span>
                  </div>
                ))}
              </div>
            </div>
            {/* Suspicious IPs */}
            {susIPs.length > 0 && (
              <div style={{ marginTop: '10px', padding: '10px', borderRadius: '8px', background: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.3)' }}>
                <div style={{ fontSize: '0.7rem', fontWeight: 700, color: '#fca5a5', marginBottom: '3px' }}>🚨 Suspicious IPs</div>
                {susIPs.slice(0, 3).map(([ip, c]) => (
                  <div key={ip} style={{ fontSize: '0.68rem', fontFamily: 'monospace', color: 'rgba(255,255,255,0.7)' }}>{ip} — {c} failures</div>
                ))}
              </div>
            )}
          </div>
        );
      })()}

      <div style={detailStyles.section}>
        <p style={detailStyles.sectionTitle}>Today's Summary</p>
        <div style={detailStyles.metricGrid}>
          <div style={detailStyles.metricCard}>
            <p style={detailStyles.metricLabel}>Total Logins</p>
            <p style={detailStyles.metricValue}>{totalEntries}</p>
          </div>
          <div style={detailStyles.metricCard}>
            <p style={detailStyles.metricLabel}>Successful</p>
            <p style={{ ...detailStyles.metricValue, color: '#16a34a' }}>{successCount}</p>
          </div>
          <div style={detailStyles.metricCard}>
            <p style={detailStyles.metricLabel}>Failed</p>
            <p style={{ ...detailStyles.metricValue, color: '#dc2626' }}>{failureCount}</p>
          </div>
        </div>
      </div>

      <div style={detailStyles.section}>
        <p style={detailStyles.sectionTitle}>By Role</p>
        <div style={detailStyles.metricGrid}>
          {Object.entries(roleBreakdown).sort((a, b) => b[1] - a[1]).map(([role, count]) => (
            <div key={role} style={detailStyles.metricCard}>
              <p style={detailStyles.metricLabel}>{role}</p>
              <p style={detailStyles.metricValue}>{count}</p>
            </div>
          ))}
        </div>
      </div>

      {Object.keys(failureReasons).length > 0 && (
        <div style={detailStyles.section}>
          <p style={detailStyles.sectionTitle}>Failure Reasons</p>
          {Object.entries(failureReasons).map(([reason, count]) => (
            <div key={reason} style={detailStyles.infoRow}>
              <span style={detailStyles.infoLabel}>{reason.replace(/_/g, ' ')}</span>
              <span style={{ ...detailStyles.infoValue, color: '#dc2626' }}>{count}</span>
            </div>
          ))}
        </div>
      )}

      {/* Top Locations */}
      {topLocations.length > 0 && (
        <div style={detailStyles.section}>
          <p style={detailStyles.sectionTitle}>📍 Top Locations</p>
          <div style={detailStyles.metricGrid}>
            {topLocations.map(([loc, count]) => (
              <div key={loc} style={detailStyles.metricCard}>
                <p style={detailStyles.metricLabel}>{loc}</p>
                <p style={detailStyles.metricValue}>{count}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Mobile vs Desktop */}
      <div style={detailStyles.section}>
        <p style={detailStyles.sectionTitle}>📱 Device Breakdown</p>
        <div style={detailStyles.metricGrid}>
          <div style={detailStyles.metricCard}>
            <p style={detailStyles.metricLabel}>📱 Mobile</p>
            <p style={detailStyles.metricValue}>{deviceStats.mobile}</p>
          </div>
          <div style={detailStyles.metricCard}>
            <p style={detailStyles.metricLabel}>🖥️ Desktop</p>
            <p style={detailStyles.metricValue}>{deviceStats.desktop}</p>
          </div>
          <div style={detailStyles.metricCard}>
            <p style={detailStyles.metricLabel}>Unique IPs</p>
            <p style={detailStyles.metricValue}>{new Set(entries.map((e) => e.ipAddress)).size}</p>
          </div>
        </div>
      </div>

      <div style={detailStyles.section}>
        <p style={detailStyles.sectionTitle}>
          Login Attempts ({entries.length} of {totalEntries})
        </p>
        <div style={{ overflowX: 'auto' }}>
          <table style={detailStyles.table}>
            <thead>
              <tr>
                <th style={detailStyles.th}>Time</th>
                <th style={detailStyles.th}>Identifier</th>
                <th style={detailStyles.th}>Name</th>
                <th style={detailStyles.th}>Status</th>
                <th style={detailStyles.th}>Reason</th>
                <th style={detailStyles.th}>Location</th>
                <th style={detailStyles.th}>IP Address</th>
                <th style={detailStyles.th}>Device</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((entry) => (
                <tr
                  key={entry.id}
                  style={{
                    backgroundColor: entry.outcome === 'failure' ? '#fef2f2' : undefined,
                  }}
                >
                  <td style={{ ...detailStyles.td, whiteSpace: 'nowrap', fontSize: '0.78rem' }}>
                    {formatTime(entry.timestamp)}
                  </td>
                  <td style={{ ...detailStyles.td, fontWeight: 600, fontSize: '0.82rem' }}>
                    {entry.identifier}
                    {entry.userRole && (
                      <span style={{ display: 'block', fontSize: '0.7rem', color: TEXT_MUTED, fontWeight: 400 }}>
                        {entry.userRole}
                      </span>
                    )}
                  </td>
                  <td style={{ ...detailStyles.td, fontSize: '0.8rem', color: '#374151' }}>
                    {entry.fullName || '—'}
                  </td>
                  <td style={detailStyles.td}>
                    <span
                      style={{
                        display: 'inline-block',
                        padding: '2px 8px',
                        borderRadius: '10px',
                        fontSize: '0.72rem',
                        fontWeight: 600,
                        color: entry.outcome === 'success' ? '#166534' : '#991b1b',
                        backgroundColor: entry.outcome === 'success' ? '#dcfce7' : '#fee2e2',
                      }}
                    >
                      {entry.outcome === 'success' ? '✓ Success' : '✗ Failed'}
                    </span>
                  </td>
                  <td style={{ ...detailStyles.td, fontSize: '0.75rem', color: '#dc2626' }}>
                    {entry.failureReason ? entry.failureReason.replace(/_/g, ' ') : '—'}
                  </td>
                  <td style={{ ...detailStyles.td, fontSize: '0.75rem', whiteSpace: 'nowrap' }}>
                    {entry.city || entry.country
                      ? `${entry.city || ''}${entry.city && entry.countryCode ? ', ' : ''}${entry.countryCode || entry.country || ''}`
                      : '—'}
                  </td>
                  <td style={{ ...detailStyles.td, fontFamily: 'monospace', fontSize: '0.75rem' }}>
                    {entry.ipAddress}
                  </td>
                  <td style={{ ...detailStyles.td, fontSize: '0.75rem', color: TEXT_MUTED }}>
                    {parseUA(entry.userAgent)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Load More button */}
        {hasMore && (
          <div style={{ textAlign: 'center', marginTop: '16px' }}>
            <button
              onClick={handleLoadMore}
              disabled={loadingMore}
              style={{
                padding: '10px 24px',
                backgroundColor: PRIMARY,
                color: '#fff',
                border: 'none',
                borderRadius: '8px',
                fontSize: '0.85rem',
                fontWeight: 600,
                cursor: loadingMore ? 'not-allowed' : 'pointer',
                opacity: loadingMore ? 0.6 : 1,
              }}
            >
              {loadingMore ? 'Loading...' : `Load Next ${PAGE_SIZE} →`}
            </button>
            <p style={{ fontSize: '0.72rem', color: TEXT_MUTED, marginTop: '6px' }}>
              Showing {entries.length} of {totalEntries} entries
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

// ── DataAuditEntriesTable (paginated) ─────────────────────────

function DataAuditEntriesTable() {
  const [entries, setEntries] = React.useState<Array<{ id: string; modelName: string; action: string; actorName: string | null; timestamp: string }>>([]);
  const [total, setTotal] = React.useState(0);
  const [loadingMore, setLoadingMore] = React.useState(false);
  const [initialLoaded, setInitialLoaded] = React.useState(false);
  const PAGE_SIZE = 20;

  const loadEntries = React.useCallback(async (offset: number) => {
    setLoadingMore(true);
    try {
      const data = await apiClient<{ entries: typeof entries; total: number }>(`/observability/audit-entries?limit=${PAGE_SIZE}&offset=${offset}`);
      if (offset === 0) {
        setEntries(data.entries);
      } else {
        setEntries((prev) => [...prev, ...data.entries]);
      }
      setTotal(data.total);
    } catch { /* silent */ }
    finally { setLoadingMore(false); setInitialLoaded(true); }
  }, []);

  React.useEffect(() => { loadEntries(0); }, [loadEntries]);

  const hasMore = entries.length < total;

  return (
    <div>
      <div style={{ overflowX: 'auto' }}>
        <table style={detailStyles.table}>
          <thead>
            <tr>
              <th style={detailStyles.th}>Model</th>
              <th style={detailStyles.th}>Action</th>
              <th style={detailStyles.th}>Actor</th>
              <th style={detailStyles.th}>Timestamp</th>
            </tr>
          </thead>
          <tbody>
            {entries.map((entry) => (
              <tr key={entry.id}>
                <td style={detailStyles.td}>{entry.modelName}</td>
                <td style={detailStyles.td}>
                  <span style={{ fontSize: '0.75rem', fontWeight: 600, padding: '2px 8px', borderRadius: '4px', backgroundColor: BG_PAGE, color: TEXT_DARK }}>
                    {entry.action}
                  </span>
                </td>
                <td style={detailStyles.td}>{entry.actorName || '—'}</td>
                <td style={detailStyles.td}>{new Date(entry.timestamp).toLocaleString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {!initialLoaded && <p style={{ textAlign: 'center', color: TEXT_MUTED, fontSize: '0.85rem' }}>Loading...</p>}
      {initialLoaded && entries.length === 0 && <p style={{ textAlign: 'center', color: TEXT_MUTED, fontSize: '0.85rem' }}>No audit entries found.</p>}
      {hasMore && (
        <div style={{ textAlign: 'center', marginTop: '12px' }}>
          <button
            onClick={() => loadEntries(entries.length)}
            disabled={loadingMore}
            style={{ padding: '8px 20px', fontSize: '0.85rem', fontWeight: 600, border: `1px solid ${BORDER_LIGHT}`, borderRadius: '6px', backgroundColor: BG_SURFACE, color: TEXT_DARK, cursor: loadingMore ? 'not-allowed' : 'pointer' }}
          >
            {loadingMore ? 'Loading...' : `Load Next ${PAGE_SIZE} (${entries.length} of ${total})`}
          </button>
        </div>
      )}
      {!hasMore && entries.length > 0 && (
        <p style={{ textAlign: 'center', color: TEXT_MUTED, fontSize: '0.8rem', marginTop: '8px' }}>
          Showing all {total} entries
        </p>
      )}
    </div>
  );
}

// ── DataAuditDetail ───────────────────────────────────────────

const DataAuditDetail: React.FC<DetailPanelProps & { data: AuditSummaryResponse | null }> = ({
  data,
  loading,
  error,
  onRetry,
}) => {
  if (loading) {
    return (
      <div style={detailStyles.loadingContainer}>
        <span>⏳ Loading data audit summary...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div style={detailStyles.errorContainer}>
        <span style={detailStyles.errorText}>⚠️ {error}</span>
        <button style={detailStyles.retryButton} onClick={onRetry}>Retry</button>
      </div>
    );
  }

  if (!data) return null;

  const actionTypes: Array<{ key: keyof typeof data.byAction; label: string }> = [
    { key: 'CREATE', label: 'Create' },
    { key: 'UPDATE', label: 'Update' },
    { key: 'DELETE', label: 'Delete' },
    { key: 'BULK_CREATE', label: 'Bulk Create' },
    { key: 'BULK_UPDATE', label: 'Bulk Update' },
    { key: 'BULK_DELETE', label: 'Bulk Delete' },
  ];

  return (
    <div>
      <h2 style={detailStyles.heading}>📝 Data Audit</h2>

      <div style={detailStyles.section}>
        <p style={detailStyles.sectionTitle}>Total Changes Today</p>
        <p style={{ ...detailStyles.metricValue, fontSize: '1.5rem', margin: '0 0 1rem 0' }}>
          {data.totalToday}
        </p>
      </div>

      <div style={detailStyles.section}>
        <p style={detailStyles.sectionTitle}>Breakdown by Action</p>
        <div style={detailStyles.metricGrid}>
          {actionTypes.map(({ key, label }) => (
            <div key={String(key)} style={detailStyles.metricCard}>
              <p style={detailStyles.metricLabel}>{label}</p>
              <p style={detailStyles.metricValue}>{data.byAction[key]}</p>
            </div>
          ))}
        </div>
      </div>

      {data.topModels.length > 0 && (
        <div style={detailStyles.section}>
          <p style={detailStyles.sectionTitle}>Top 5 Most-Modified Models</p>
          {data.topModels.map((model, idx) => (
            <div key={model.modelName} style={detailStyles.infoRow}>
              <span style={detailStyles.infoLabel}>
                {idx + 1}. {model.modelName}
              </span>
              <span style={detailStyles.infoValue}>{model.count} changes</span>
            </div>
          ))}
        </div>
      )}

      <div style={detailStyles.section}>
        <p style={detailStyles.sectionTitle}>Recent Audit Entries (last 10)</p>
        <DataAuditEntriesTable />
      </div>
    </div>
  );
};

// ── ErrorReportsDetail ────────────────────────────────────────

const ErrorReportsDetail: React.FC<DetailPanelProps & { data: ErrorReportsResponse | null }> = ({
  data,
  loading,
  error,
  onRetry,
}) => {
  if (loading) {
    return (
      <div style={detailStyles.loadingContainer}>
        <span>⏳ Loading error reports...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div style={detailStyles.errorContainer}>
        <span style={detailStyles.errorText}>⚠️ {error}</span>
        <button style={detailStyles.retryButton} onClick={onRetry}>Retry</button>
      </div>
    );
  }

  if (!data) return null;

  const openCount = data.data.filter((r) => r.status === 'open').length;
  const resolvedCount = data.data.filter((r) => r.status === 'resolved').length;

  const todayStr = new Date().toISOString().split('T')[0];
  const todayCount = data.data.filter((r) => r.createdAt.split('T')[0] === todayStr).length;

  // Status breakdown across all reports in the current page
  const statusBreakdown: Record<string, number> = {};
  data.data.forEach((r) => {
    statusBreakdown[r.status] = (statusBreakdown[r.status] || 0) + 1;
  });

  return (
    <div>
      <h2 style={detailStyles.heading}>🐛 Error Reports</h2>

      <div style={detailStyles.section}>
        <p style={detailStyles.sectionTitle}>Summary</p>
        <div style={detailStyles.metricGrid}>
          <div style={detailStyles.metricCard}>
            <p style={detailStyles.metricLabel}>Total Reports</p>
            <p style={detailStyles.metricValue}>{data.total}</p>
          </div>
          <div style={detailStyles.metricCard}>
            <p style={detailStyles.metricLabel}>Open</p>
            <p style={{ ...detailStyles.metricValue, color: '#dc2626' }}>{openCount}</p>
          </div>
          <div style={detailStyles.metricCard}>
            <p style={detailStyles.metricLabel}>Resolved</p>
            <p style={{ ...detailStyles.metricValue, color: '#16a34a' }}>{resolvedCount}</p>
          </div>
          <div style={detailStyles.metricCard}>
            <p style={detailStyles.metricLabel}>Submitted Today</p>
            <p style={detailStyles.metricValue}>{todayCount}</p>
          </div>
        </div>
      </div>

      {Object.keys(statusBreakdown).length > 0 && (
        <div style={detailStyles.section}>
          <p style={detailStyles.sectionTitle}>Breakdown by Status</p>
          {Object.entries(statusBreakdown).map(([status, count]) => (
            <div key={status} style={detailStyles.infoRow}>
              <span style={detailStyles.infoLabel}>
                <span
                  style={{
                    ...detailStyles.statusBadge,
                    color: getStatusColor(status),
                    backgroundColor: getStatusBgColor(status),
                    fontSize: '0.75rem',
                  }}
                >
                  ● {status}
                </span>
              </span>
              <span style={detailStyles.infoValue}>{count}</span>
            </div>
          ))}
        </div>
      )}

      <div style={detailStyles.section}>
        <p style={detailStyles.sectionTitle}>Recent Error Reports (last 5)</p>
        <div style={{ overflowX: 'auto' }}>
          <table style={detailStyles.table}>
            <thead>
              <tr>
                <th style={detailStyles.th}>Title (Screen)</th>
                <th style={detailStyles.th}>Status</th>
                <th style={detailStyles.th}>Reporter</th>
                <th style={detailStyles.th}>Submitted</th>
              </tr>
            </thead>
            <tbody>
              {data.data.slice(0, 5).map((report) => (
                <tr key={report.id}>
                  <td style={detailStyles.td}>{report.screenSlug}</td>
                  <td style={detailStyles.td}>
                    <span
                      style={{
                        ...detailStyles.statusBadge,
                        color: getStatusColor(report.status),
                        backgroundColor: getStatusBgColor(report.status),
                        fontSize: '0.75rem',
                      }}
                    >
                      ● {report.status}
                    </span>
                  </td>
                  <td style={detailStyles.td}>{report.reporterName}</td>
                  <td style={detailStyles.td}>
                    {new Date(report.createdAt).toLocaleString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// ── DataIntegrityDetail ────────────────────────────────────────

const DataIntegrityDetail: React.FC<DetailPanelProps & { data: UnlinkedTicketsResponse | null }> = ({
  data,
  loading,
  error,
  onRetry,
}) => {
  if (loading) {
    return (
      <div style={detailStyles.loadingContainer}>
        <span>⏳ Loading data integrity data...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div style={detailStyles.errorContainer}>
        <span style={detailStyles.errorText}>⚠️ {error}</span>
        <button style={detailStyles.retryButton} onClick={onRetry}>Retry</button>
      </div>
    );
  }

  if (!data) return null;

  return (
    <div>
      <h2 style={detailStyles.heading}>🔗 Data Integrity</h2>

      <div style={detailStyles.section}>
        <p style={detailStyles.sectionTitle}>Total Unlinked Tickets</p>
        <p style={{ ...detailStyles.metricValue, fontSize: '1.5rem', margin: '0 0 1rem 0' }}>
          {data.total}
        </p>
      </div>

      <div style={detailStyles.section}>
        <p style={detailStyles.sectionTitle}>Unlinked Tickets</p>
        <div style={{ overflowX: 'auto' }}>
          <table style={detailStyles.table}>
            <thead>
              <tr>
                <th style={detailStyles.th}>Ticket #</th>
                <th style={detailStyles.th}>Guest Name</th>
                <th style={detailStyles.th}>Meal</th>
                <th style={detailStyles.th}>Tier</th>
                <th style={detailStyles.th}>Order Ref</th>
                <th style={detailStyles.th}>Status</th>
              </tr>
            </thead>
            <tbody>
              {data.tickets.map((ticket) => (
                <tr key={ticket.ticketNumber}>
                  <td style={{ ...detailStyles.td, fontFamily: 'monospace' }}>{ticket.formattedTicket}</td>
                  <td style={detailStyles.td}>{ticket.guestName || '—'}</td>
                  <td style={detailStyles.td}>{ticket.mealOption || '—'}</td>
                  <td style={detailStyles.td}>{ticket.tierName}</td>
                  <td style={{ ...detailStyles.td, fontFamily: 'monospace' }}>{ticket.orderRef || '—'}</td>
                  <td style={detailStyles.td}>
                    <span
                      style={{
                        ...detailStyles.statusBadge,
                        color: getStatusColor(ticket.status),
                        backgroundColor: getStatusBgColor(ticket.status),
                        fontSize: '0.75rem',
                      }}
                    >
                      ● {ticket.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// ── GuestAccountsDetail ────────────────────────────────────────

const ROLE_DESCRIPTIONS: Record<string, { emoji: string; summary: string; access: string }> = {
  Admin: {
    emoji: '🛡️',
    summary: 'Full system access — manages users, settings, and all platform data.',
    access: 'Level 4 — Can access everything including user management, page access controls, and system configuration.',
  },
  Organizer: {
    emoji: '📋',
    summary: 'Event management access — plans events, manages registrations, and coordinates logistics.',
    access: 'Level 3 — Can manage events, booklet, tickets, table assignments, and view reports.',
  },
  Delegate: {
    emoji: '🎖️',
    summary: 'Extended member access — chapter representatives with enhanced participation privileges.',
    access: 'Level 2 — Can access delegate-only announcements, chapter tools, and extended event features.',
  },
  Attendee: {
    emoji: '👤',
    summary: 'Basic authenticated access — guests attending the reunion with personal dashboards.',
    access: 'Level 1 — Can view their ticket, meal, schedule, and personal My Weekend page.',
  },
};

const GuestAccountsDetail: React.FC<DetailPanelProps & { data: GuestAccountsResponse | null }> = ({
  data,
  loading,
  error,
  onRetry,
}) => {
  const [selectedRole, setSelectedRole] = useState<string | null>(null);
  const [roleUsers, setRoleUsers] = useState<any[] | null>(null);
  const [loadingUsers, setLoadingUsers] = useState(false);

  const handleRoleClick = async (role: string) => {
    if (selectedRole === role) {
      setSelectedRole(null);
      setRoleUsers(null);
      return;
    }
    setSelectedRole(role);
    setLoadingUsers(true);
    try {
      const result = await apiClient<{ role: string; total: number; users: any[] }>(
        '/observability/users-by-role?role=' + role,
      );
      setRoleUsers(result.users);
    } catch {
      setRoleUsers([]);
    } finally {
      setLoadingUsers(false);
    }
  };

  if (loading) {
    return (
      <div style={detailStyles.loadingContainer}>
        <span>⏳ Loading guest accounts data...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div style={detailStyles.errorContainer}>
        <span style={detailStyles.errorText}>⚠️ {error}</span>
        <button style={detailStyles.retryButton} onClick={onRetry}>Retry</button>
      </div>
    );
  }

  if (!data) return null;

  const roleDesc = selectedRole ? ROLE_DESCRIPTIONS[selectedRole] : null;

  return (
    <div>
      <h2 style={detailStyles.heading}>👤 Guest Accounts</h2>

      <div style={detailStyles.section}>
        <p style={detailStyles.sectionTitle}>Account Overview</p>
        <div style={detailStyles.metricGrid}>
          <div style={detailStyles.metricCard}>
            <p style={detailStyles.metricLabel}>Total Users</p>
            <p style={detailStyles.metricValue}>{data.totalUsers}</p>
          </div>
          <div style={detailStyles.metricCard}>
            <p style={detailStyles.metricLabel}>Synthetic Accounts</p>
            <p style={detailStyles.metricValue}>{data.syntheticAccounts}</p>
          </div>
          <div style={detailStyles.metricCard}>
            <p style={detailStyles.metricLabel}>Pending Login</p>
            <p style={{ ...detailStyles.metricValue, color: '#ca8a04' }}>{data.mustChangePassword}</p>
          </div>
          <div style={detailStyles.metricCard}>
            <p style={detailStyles.metricLabel}>Disabled</p>
            <p style={{ ...detailStyles.metricValue, color: '#dc2626' }}>{data.disabledAccounts}</p>
          </div>
        </div>
      </div>

      <div style={detailStyles.section}>
        <p style={detailStyles.sectionTitle}>By Role (tap to view users)</p>
        <div style={detailStyles.metricGrid}>
          {(Object.entries(data.byRole) as Array<[string, number]>).map(([role, count]) => {
            const desc = ROLE_DESCRIPTIONS[role];
            return (
              <div
                key={role}
                style={{
                  ...detailStyles.metricCard,
                  cursor: 'pointer',
                  border: selectedRole === role ? `2px solid ${PRIMARY}` : `1px solid ${BORDER_LIGHT}`,
                  padding: selectedRole === role ? '9px 11px' : '10px 12px',
                  transition: 'border-color 0.15s, box-shadow 0.15s',
                  boxShadow: selectedRole === role ? `0 0 0 1px ${PRIMARY}20` : undefined,
                }}
                onClick={() => handleRoleClick(role)}
                role="button"
                tabIndex={0}
                aria-label={`View ${role} users`}
                aria-pressed={selectedRole === role}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleRoleClick(role); } }}
              >
                <p style={detailStyles.metricLabel}>{desc?.emoji} {role}</p>
                <p style={detailStyles.metricValue}>{count}</p>
              </div>
            );
          })}
        </div>
      </div>

      {selectedRole && roleDesc && (
        <div style={{
          ...detailStyles.section,
          backgroundColor: `${PRIMARY}08`,
          borderRadius: '8px',
          border: `1px solid ${PRIMARY}30`,
          padding: '12px 16px',
          marginBottom: '1.5rem',
        }}>
          <p style={{ ...detailStyles.sectionTitle, margin: '0 0 6px 0', fontSize: '0.95rem' }}>
            {roleDesc.emoji} {selectedRole} — At a Glance
          </p>
          <p style={{ fontSize: '0.85rem', color: TEXT_DARK, margin: '0 0 6px 0' }}>
            {roleDesc.summary}
          </p>
          <p style={{ fontSize: '0.8rem', color: TEXT_MUTED, margin: 0 }}>
            {roleDesc.access}
          </p>
        </div>
      )}

      {selectedRole && (
        <div style={detailStyles.section}>
          {loadingUsers ? (
            <div style={detailStyles.loadingContainer}>
              <span>⏳ Loading {selectedRole} users...</span>
            </div>
          ) : roleUsers && (
            <>
              <p style={detailStyles.sectionTitle}>{selectedRole} Users ({roleUsers.length})</p>
              <div style={{ overflowX: 'auto' }}>
                <table style={detailStyles.table}>
                  <thead>
                    <tr>
                      <th style={detailStyles.th}>Name</th>
                      <th style={detailStyles.th}>Email</th>
                      <th style={detailStyles.th}>Synthetic</th>
                      <th style={detailStyles.th}>Status</th>
                      <th style={detailStyles.th}>Created</th>
                    </tr>
                  </thead>
                  <tbody>
                    {roleUsers.length === 0 ? (
                      <tr>
                        <td colSpan={5} style={{ ...detailStyles.td, textAlign: 'center', color: TEXT_MUTED }}>
                          No users found for this role
                        </td>
                      </tr>
                    ) : roleUsers.map((user) => (
                      <tr key={user.id}>
                        <td style={detailStyles.td}>{user.fullName || '—'}</td>
                        <td style={{ ...detailStyles.td, fontSize: '0.75rem', wordBreak: 'break-all' }}>{user.email || '—'}</td>
                        <td style={detailStyles.td}>{user.isSynthetic ? 'Yes' : 'No'}</td>
                        <td style={detailStyles.td}>
                          <span
                            style={{
                              ...detailStyles.statusBadge,
                              color: user.deactivatedAt ? '#dc2626' : '#16a34a',
                              backgroundColor: user.deactivatedAt ? '#fee2e2' : '#dcfce7',
                              fontSize: '0.75rem',
                            }}
                          >
                            ● {user.deactivatedAt ? 'Disabled' : 'Active'}
                          </span>
                        </td>
                        <td style={detailStyles.td}>
                          {user.createdAt ? new Date(user.createdAt).toLocaleDateString() : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

// ── RegistrationStatsDetail ────────────────────────────────────

const RegistrationStatsDetail: React.FC<DetailPanelProps & { data: RegistrationStatsResponse | null }> = ({
  data,
  loading,
  error,
  onRetry,
}) => {
  const [selectedChapter, setSelectedChapter] = useState<string | null>(null);
  const [chapterGuests, setChapterGuests] = useState<any[] | null>(null);
  const [loadingGuests, setLoadingGuests] = useState(false);
  const [viewMode, setViewMode] = useState<'no-meal' | 'all'>('no-meal');

  const handleChapterClick = async (chapter: string, noMeal: number) => {
    if (selectedChapter === chapter) {
      setSelectedChapter(null);
      setChapterGuests(null);
      return;
    }
    setSelectedChapter(chapter);
    setLoadingGuests(true);
    const mode = noMeal > 0 ? 'no-meal' : 'all';
    setViewMode(mode);
    try {
      const endpoint = mode === 'no-meal'
        ? '/observability/no-meal-by-chapter?chapter=' + encodeURIComponent(chapter)
        : '/observability/guests-by-chapter?chapter=' + encodeURIComponent(chapter);
      const result = await apiClient<{ chapter: string; total: number; guests: any[] }>(endpoint);
      setChapterGuests(result.guests);
    } catch {
      setChapterGuests([]);
    } finally {
      setLoadingGuests(false);
    }
  };

  if (loading) {
    return (
      <div style={detailStyles.loadingContainer}>
        <span>⏳ Loading registration stats...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div style={detailStyles.errorContainer}>
        <span style={detailStyles.errorText}>⚠️ {error}</span>
        <button style={detailStyles.retryButton} onClick={onRetry}>Retry</button>
      </div>
    );
  }

  if (!data) return null;

  return (
    <div>
      <h2 style={detailStyles.heading}>🎫 Registration Stats</h2>

      <div style={detailStyles.section}>
        <p style={detailStyles.sectionTitle}>Overview</p>
        <div style={detailStyles.metricGrid}>
          <div style={detailStyles.metricCard}>
            <p style={detailStyles.metricLabel}>Total Guests</p>
            <p style={detailStyles.metricValue}>{data.totalGuestRecords}</p>
          </div>
          <div style={detailStyles.metricCard}>
            <p style={detailStyles.metricLabel}>Registrations</p>
            <p style={detailStyles.metricValue}>{data.totalRegistrations}</p>
          </div>
          <div style={detailStyles.metricCard}>
            <p style={detailStyles.metricLabel}>Checked In</p>
            <p style={{ ...detailStyles.metricValue, color: '#16a34a' }}>{data.checkedIn}</p>
          </div>
          <div style={detailStyles.metricCard}>
            <p style={detailStyles.metricLabel}>Unseated</p>
            <p style={{ ...detailStyles.metricValue, color: '#ca8a04' }}>{data.unseatedGuests}</p>
          </div>
          <div style={detailStyles.metricCard}>
            <p style={detailStyles.metricLabel}>No Meal</p>
            <p style={{ ...detailStyles.metricValue, color: '#dc2626' }}>{data.guestsWithoutMeal}</p>
          </div>
        </div>
      </div>

      {data.byChapter.length > 0 && (
        <div style={detailStyles.section}>
          <p style={detailStyles.sectionTitle}>By Chapter</p>
          <div style={{ overflowX: 'auto' }}>
            <table style={detailStyles.table}>
              <thead>
                <tr>
                  <th style={detailStyles.th}>Chapter</th>
                  <th style={detailStyles.th}>Guests</th>
                  <th style={detailStyles.th}>No Meal</th>
                </tr>
              </thead>
              <tbody>
                {data.byChapter.map((row) => (
                  <tr
                    key={row.chapter}
                    onClick={() => handleChapterClick(row.chapter, row.noMeal)}
                    style={{
                      cursor: 'pointer',
                      backgroundColor: selectedChapter === row.chapter ? `${PRIMARY}10` : undefined,
                      transition: 'background-color 0.15s',
                    }}
                    role="button"
                    tabIndex={0}
                    aria-label={`View guests in ${row.chapter}${row.noMeal > 0 ? ' (has guests with no meal)' : ''}`}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleChapterClick(row.chapter, row.noMeal); } }}
                  >
                    <td style={{ ...detailStyles.td, fontWeight: selectedChapter === row.chapter ? 600 : undefined }}>{row.chapter}</td>
                    <td style={detailStyles.td}>{row.count}</td>
                    <td style={detailStyles.td}>
                      <span style={{ color: row.noMeal > 0 ? '#dc2626' : '#16a34a', fontWeight: 600 }}>
                        {row.noMeal}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {selectedChapter && (
        <div style={detailStyles.section}>
          {loadingGuests ? (
            <div style={detailStyles.loadingContainer}>
              <span>⏳ Loading guests in {selectedChapter}...</span>
            </div>
          ) : chapterGuests && (
            <>
              <p style={detailStyles.sectionTitle}>
                {viewMode === 'no-meal'
                  ? `Guests with No Meal in ${selectedChapter} (${chapterGuests.length})`
                  : `All Guests in ${selectedChapter} (${chapterGuests.length})`
                }
              </p>
              <div style={{ overflowX: 'auto' }}>
                <table style={detailStyles.table}>
                  <thead>
                    <tr>
                      <th style={detailStyles.th}>Name</th>
                      <th style={detailStyles.th}>Ticket #</th>
                      {viewMode === 'no-meal' && <th style={detailStyles.th}>Order #</th>}
                      {viewMode === 'no-meal' && <th style={detailStyles.th}>Purchased By</th>}
                      {viewMode === 'all' && <th style={detailStyles.th}>Meal</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {chapterGuests.length === 0 ? (
                      <tr>
                        <td colSpan={viewMode === 'all' ? 3 : 4} style={{ ...detailStyles.td, textAlign: 'center', color: TEXT_MUTED }}>
                          No guests found
                        </td>
                      </tr>
                    ) : chapterGuests.map((guest) => (
                      <tr key={guest.id}>
                        <td style={detailStyles.td}>{guest.fullName || '—'}</td>
                        <td style={{ ...detailStyles.td, fontFamily: 'monospace' }}>{guest.ticketNumber || '—'}</td>
                        {viewMode === 'no-meal' && (
                          <td style={{ ...detailStyles.td, fontFamily: 'monospace', fontSize: '0.75rem' }}>
                            {guest.orderRef || '—'}
                          </td>
                        )}
                        {viewMode === 'no-meal' && (
                          <td style={detailStyles.td}>{guest.purchasedBy || '—'}</td>
                        )}
                        {viewMode === 'all' && (
                          <td style={detailStyles.td}>
                            <span style={{ color: guest.dietaryPreference ? '#16a34a' : '#dc2626', fontSize: '0.8rem' }}>
                              {guest.dietaryPreference || 'None'}
                            </span>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

// ── Styles ────────────────────────────────────────────────────

const styles: Record<string, React.CSSProperties> = {
  page: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    backgroundColor: BG_PAGE,
    padding: '1.5rem',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '1.5rem',
  },
  title: {
    fontSize: '1.75rem',
    fontWeight: 700,
    color: TEXT_DARK,
    margin: 0,
  },
  headerRight: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  lastUpdated: {
    fontSize: '0.8rem',
    color: TEXT_MUTED,
  },
  refreshBtn: {
    padding: '8px 16px',
    backgroundColor: PRIMARY,
    color: '#ffffff',
    border: 'none',
    borderRadius: '6px',
    fontSize: '0.85rem',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'opacity 0.15s',
  },
  panelContainer: {
    display: 'flex',
    flex: 1,
    gap: '1.5rem',
    minHeight: 0,
  },
  leftPanel: {
    width: '30%',
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  rightPanel: {
    width: '70%',
    backgroundColor: BG_CARD,
    borderRadius: '8px',
    border: `1px solid ${BORDER_LIGHT}`,
    padding: '1.5rem',
    position: 'sticky',
    top: 0,
    alignSelf: 'flex-start',
    maxHeight: 'calc(100vh - 160px)',
    overflowY: 'auto',
  },
  placeholder: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    minHeight: '300px',
    color: TEXT_MUTED,
    textAlign: 'center',
  },
  placeholderIcon: {
    fontSize: '3rem',
    marginBottom: '1rem',
  },
  placeholderText: {
    fontSize: '1rem',
    margin: 0,
  },
  placeholderSubtext: {
    fontSize: '0.85rem',
    marginTop: '0.5rem',
    color: TEXT_MUTED,
  },
  loadingText: {
    color: TEXT_MUTED,
    fontSize: '0.9rem',
    padding: '1rem',
  },
};

// ── Component ─────────────────────────────────────────────────

const ObservabilityDashboardPage: React.FC = () => {
  const [state, setState] = useState<DashboardState>(initialState);
  const isMobile = useIsMobile();

  const fetchAllData = useCallback(async () => {
    // Set all categories to loading
    setState((prev) => ({
      ...prev,
      health: { ...prev.health, loading: true, error: null },
      loginAudit: { ...prev.loginAudit, loading: true, error: null },
      dataAudit: { ...prev.dataAudit, loading: true, error: null },
      errorReports: { ...prev.errorReports, loading: true, error: null },
      dataIntegrity: { ...prev.dataIntegrity, loading: true, error: null },
      guestAccounts: { ...prev.guestAccounts, loading: true, error: null },
      registrationStats: { ...prev.registrationStats, loading: true, error: null },
    }));

    const now = new Date();
    const todayStart = now.toISOString().split('T')[0];
    const tomorrow = new Date(now);
    tomorrow.setDate(tomorrow.getDate() + 1);
    const tomorrowStr = tomorrow.toISOString().split('T')[0];

    // Fetch all in parallel
    const [healthResult, loginResult, auditResult, errorResult, dataIntegrityResult, guestAccountsResult, registrationStatsResult] = await Promise.allSettled([
      apiClient<HealthResponse>('/health'),
      apiClient<LoginAuditResponse>(`/login-audit?startDate=${todayStart}&endDate=${tomorrowStr}&page=1&pageSize=20`),
      apiClient<AuditSummaryResponse>('/observability/audit-summary'),
      apiClient<ErrorReportsResponse>('/error-reports?page=1'),
      apiClient<UnlinkedTicketsResponse>('/observability/unlinked-tickets'),
      apiClient<GuestAccountsResponse>('/observability/guest-accounts'),
      apiClient<RegistrationStatsResponse>('/observability/registration-stats'),
    ]);

    setState((prev) => ({
      ...prev,
      health: {
        data: healthResult.status === 'fulfilled' ? healthResult.value : prev.health.data,
        loading: false,
        error: healthResult.status === 'rejected' ? (healthResult.reason?.message || 'Failed to fetch') : null,
        lastFetched: healthResult.status === 'fulfilled' ? now : prev.health.lastFetched,
      },
      loginAudit: {
        data: loginResult.status === 'fulfilled' ? loginResult.value : prev.loginAudit.data,
        loading: false,
        error: loginResult.status === 'rejected' ? (loginResult.reason?.message || 'Failed to fetch') : null,
        lastFetched: loginResult.status === 'fulfilled' ? now : prev.loginAudit.lastFetched,
      },
      dataAudit: {
        data: auditResult.status === 'fulfilled' ? auditResult.value : prev.dataAudit.data,
        loading: false,
        error: auditResult.status === 'rejected' ? (auditResult.reason?.message || 'Failed to fetch') : null,
        lastFetched: auditResult.status === 'fulfilled' ? now : prev.dataAudit.lastFetched,
      },
      errorReports: {
        data: errorResult.status === 'fulfilled' ? errorResult.value : prev.errorReports.data,
        loading: false,
        error: errorResult.status === 'rejected' ? (errorResult.reason?.message || 'Failed to fetch') : null,
        lastFetched: errorResult.status === 'fulfilled' ? now : prev.errorReports.lastFetched,
      },
      dataIntegrity: {
        data: dataIntegrityResult.status === 'fulfilled' ? dataIntegrityResult.value : prev.dataIntegrity.data,
        loading: false,
        error: dataIntegrityResult.status === 'rejected' ? (dataIntegrityResult.reason?.message || 'Failed to fetch') : null,
        lastFetched: dataIntegrityResult.status === 'fulfilled' ? now : prev.dataIntegrity.lastFetched,
      },
      guestAccounts: {
        data: guestAccountsResult.status === 'fulfilled' ? guestAccountsResult.value : prev.guestAccounts.data,
        loading: false,
        error: guestAccountsResult.status === 'rejected' ? (guestAccountsResult.reason?.message || 'Failed to fetch') : null,
        lastFetched: guestAccountsResult.status === 'fulfilled' ? now : prev.guestAccounts.lastFetched,
      },
      registrationStats: {
        data: registrationStatsResult.status === 'fulfilled' ? registrationStatsResult.value : prev.registrationStats.data,
        loading: false,
        error: registrationStatsResult.status === 'rejected' ? (registrationStatsResult.reason?.message || 'Failed to fetch') : null,
        lastFetched: registrationStatsResult.status === 'fulfilled' ? now : prev.registrationStats.lastFetched,
      },
      lastUpdated: now,
    }));
  }, []);

  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  const handleRefresh = useCallback(() => {
    fetchAllData();
  }, [fetchAllData]);

  const isAnyLoading =
    state.health.loading ||
    state.loginAudit.loading ||
    state.dataAudit.loading ||
    state.errorReports.loading ||
    state.dataIntegrity.loading ||
    state.guestAccounts.loading ||
    state.registrationStats.loading;

  return (
    <div style={{ ...styles.page, padding: isMobile ? '1rem' : '1.5rem' }}>
      {/* Header */}
      <div style={{ ...styles.header, flexDirection: isMobile ? 'column' : 'row', alignItems: isMobile ? 'flex-start' : 'center', gap: isMobile ? '8px' : undefined }}>
        <h1 style={{ ...styles.title, fontSize: isMobile ? '1.25rem' : '1.75rem' }}>📊 Observability</h1>
        <div style={styles.headerRight}>
          {state.lastUpdated && (
            <span style={styles.lastUpdated}>
              Last updated: {state.lastUpdated.toLocaleTimeString()}
            </span>
          )}
          <button
            style={{
              ...styles.refreshBtn,
              opacity: isAnyLoading ? 0.6 : 1,
              cursor: isAnyLoading ? 'not-allowed' : 'pointer',
              padding: isMobile ? '6px 12px' : '8px 16px',
              fontSize: isMobile ? '0.8rem' : '0.85rem',
            }}
            onClick={handleRefresh}
            disabled={isAnyLoading}
            aria-label="Refresh all data"
          >
            🔄 Refresh
          </button>
        </div>
      </div>

      {/* Two-panel layout — stacks vertically on mobile */}
      <div style={{ ...styles.panelContainer, flexDirection: isMobile ? 'column' : 'row' }}>
        {/* Category Cards — horizontal scroll on mobile, vertical column on desktop */}
        <div
          style={isMobile
            ? { display: 'flex', flexDirection: 'row', gap: '10px', overflowX: 'auto', paddingBottom: '8px', WebkitOverflowScrolling: 'touch' as any }
            : styles.leftPanel
          }
          aria-label="Category cards"
        >
          <CategoryCard
            icon="🖥️"
            name="System Health"
            isActive={state.activeCategory === 'system-health'}
            isLoading={state.health.loading}
            hasError={!!state.health.error}
            onClick={() => setState((prev) => ({ ...prev, activeCategory: 'system-health' }))}
            onRetry={fetchAllData}
            metrics={
              state.health.data
                ? [{ label: 'Status', value: state.health.data.status }]
                : []
            }
          />
          <CategoryCard
            icon="🔐"
            name="Login Audit"
            isActive={state.activeCategory === 'login-audit'}
            isLoading={state.loginAudit.loading}
            hasError={!!state.loginAudit.error}
            onClick={() => setState((prev) => ({ ...prev, activeCategory: 'login-audit' }))}
            onRetry={fetchAllData}
            metrics={
              state.loginAudit.data
                ? [
                    { label: 'Total logins today', value: state.loginAudit.data.total },
                    { label: 'Failures', value: state.loginAudit.data.data.filter((e) => e.outcome === 'failure').length },
                  ]
                : []
            }
          />
          <CategoryCard
            icon="📝"
            name="Data Audit"
            isActive={state.activeCategory === 'data-audit'}
            isLoading={state.dataAudit.loading}
            hasError={!!state.dataAudit.error}
            onClick={() => setState((prev) => ({ ...prev, activeCategory: 'data-audit' }))}
            onRetry={fetchAllData}
            metrics={
              state.dataAudit.data
                ? [{ label: 'Total today', value: state.dataAudit.data.totalToday }]
                : []
            }
          />
          <CategoryCard
            icon="🐛"
            name="Error Reports"
            isActive={state.activeCategory === 'error-reports'}
            isLoading={state.errorReports.loading}
            hasError={!!state.errorReports.error}
            onClick={() => setState((prev) => ({ ...prev, activeCategory: 'error-reports' }))}
            onRetry={fetchAllData}
            metrics={
              state.errorReports.data
                ? [
                    { label: 'Total', value: state.errorReports.data.total },
                    { label: 'Open', value: state.errorReports.data.data.filter((e) => e.status === 'open').length },
                  ]
                : []
            }
          />
          <CategoryCard
            icon="🔗"
            name="Data Integrity"
            isActive={state.activeCategory === 'data-integrity'}
            isLoading={state.dataIntegrity.loading}
            hasError={!!state.dataIntegrity.error}
            onClick={() => setState((prev) => ({ ...prev, activeCategory: 'data-integrity' }))}
            onRetry={fetchAllData}
            metrics={
              state.dataIntegrity.data
                ? [{ label: 'Unlinked tickets', value: state.dataIntegrity.data.total }]
                : []
            }
          />
          <CategoryCard
            icon="👤"
            name="Guest Accounts"
            isActive={state.activeCategory === 'guest-accounts'}
            isLoading={state.guestAccounts.loading}
            hasError={!!state.guestAccounts.error}
            onClick={() => setState((prev) => ({ ...prev, activeCategory: 'guest-accounts' }))}
            onRetry={fetchAllData}
            metrics={
              state.guestAccounts.data
                ? [
                    { label: 'Total users', value: state.guestAccounts.data.totalUsers },
                    { label: 'Pending login', value: state.guestAccounts.data.mustChangePassword },
                  ]
                : []
            }
          />
          <CategoryCard
            icon="🎫"
            name="Registration Stats"
            isActive={state.activeCategory === 'registration-stats'}
            isLoading={state.registrationStats.loading}
            hasError={!!state.registrationStats.error}
            onClick={() => setState((prev) => ({ ...prev, activeCategory: 'registration-stats' }))}
            onRetry={fetchAllData}
            metrics={
              state.registrationStats.data
                ? [
                    { label: 'Total guests', value: state.registrationStats.data.totalGuestRecords },
                    { label: 'Checked in', value: state.registrationStats.data.checkedIn },
                  ]
                : []
            }
          />
          <CategoryCard
            icon="📥"
            name="Data Downloads"
            isActive={state.activeCategory === 'data-downloads'}
            isLoading={false}
            hasError={false}
            onClick={() => setState((prev) => ({ ...prev, activeCategory: 'data-downloads' }))}
            onRetry={fetchAllData}
            metrics={[]}
          />
        </div>

        {/* Right Panel - Detail View (full width on mobile, 70% on desktop) */}
        <div style={isMobile
          ? { width: '100%', backgroundColor: BG_CARD, borderRadius: '8px', border: `1px solid ${BORDER_LIGHT}`, padding: '1rem' }
          : styles.rightPanel
        } aria-label="Detail panel">
          {!state.activeCategory && (
            <div style={styles.placeholder}>
              <span style={styles.placeholderIcon}>📋</span>
              <p style={styles.placeholderText}>Select a category to view details</p>
              <p style={styles.placeholderSubtext}>
                Click one of the category cards on the left to see detailed information
              </p>
            </div>
          )}
          {state.activeCategory === 'system-health' && (
            <SystemHealthDetail
              data={state.health.data}
              loading={state.health.loading}
              error={state.health.error}
              onRetry={fetchAllData}
            />
          )}
          {state.activeCategory === 'login-audit' && (
            <LoginAuditDetail
              data={state.loginAudit.data}
              loading={state.loginAudit.loading}
              error={state.loginAudit.error}
              onRetry={fetchAllData}
            />
          )}
          {state.activeCategory === 'data-audit' && (
            <DataAuditDetail
              data={state.dataAudit.data}
              loading={state.dataAudit.loading}
              error={state.dataAudit.error}
              onRetry={fetchAllData}
            />
          )}
          {state.activeCategory === 'error-reports' && (
            <ErrorReportsDetail
              data={state.errorReports.data}
              loading={state.errorReports.loading}
              error={state.errorReports.error}
              onRetry={fetchAllData}
            />
          )}
          {state.activeCategory === 'data-integrity' && (
            <DataIntegrityDetail
              data={state.dataIntegrity.data}
              loading={state.dataIntegrity.loading}
              error={state.dataIntegrity.error}
              onRetry={fetchAllData}
            />
          )}
          {state.activeCategory === 'guest-accounts' && (
            <GuestAccountsDetail
              data={state.guestAccounts.data}
              loading={state.guestAccounts.loading}
              error={state.guestAccounts.error}
              onRetry={fetchAllData}
            />
          )}
          {state.activeCategory === 'registration-stats' && (
            <RegistrationStatsDetail
              data={state.registrationStats.data}
              loading={state.registrationStats.loading}
              error={state.registrationStats.error}
              onRetry={fetchAllData}
            />
          )}
          {state.activeCategory === 'data-downloads' && (
            <DataDownloadsDetail />
          )}
        </div>
      </div>
    </div>
  );
};

// ── DataDownloadsDetail ─────────────────────────────────────────────────────

function DataDownloadsDetail() {
  const [downloads, setDownloads] = React.useState<Array<{
    id: string;
    fullName: string | null;
    action: string;
    fileName: string | null;
    ipAddress: string | null;
    timestamp: string;
    user: { fullName: string | null; email: string; role: string };
  }>>([]);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    apiClient<typeof downloads>('/audit/data-downloads?limit=50')
      .then(setDownloads)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return (
    <div>
      <h2 style={detailStyles.heading}>📥 Data Downloads</h2>
      <p style={{ fontSize: '0.82rem', color: TEXT_MUTED, marginBottom: '1rem' }}>
        All data export and download activity. Newest first.
      </p>
      {loading && <p style={{ color: TEXT_MUTED, fontSize: '0.85rem' }}>Loading download audit...</p>}
      {!loading && downloads.length === 0 && (
        <p style={{ color: TEXT_MUTED, fontSize: '0.85rem' }}>No downloads recorded yet.</p>
      )}
      {!loading && downloads.length > 0 && (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.78rem' }}>
            <thead>
              <tr style={{ borderBottom: `2px solid ${BORDER_LIGHT}` }}>
                <th style={detailStyles.th}>Time</th>
                <th style={detailStyles.th}>User</th>
                <th style={detailStyles.th}>Action</th>
                <th style={detailStyles.th}>File</th>
                <th style={detailStyles.th}>IP</th>
              </tr>
            </thead>
            <tbody>
              {downloads.map((d) => (
                <tr key={d.id} style={{ borderBottom: `1px solid ${BORDER_LIGHT}` }}>
                  <td style={{ ...detailStyles.td, whiteSpace: 'nowrap', fontSize: '0.75rem' }}>
                    {new Date(d.timestamp).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                  </td>
                  <td style={{ ...detailStyles.td, fontWeight: 600 }}>
                    {d.user?.fullName || d.fullName || '—'}
                    <span style={{ display: 'block', fontSize: '0.68rem', color: TEXT_MUTED, fontWeight: 400 }}>{d.user?.role}</span>
                  </td>
                  <td style={detailStyles.td}>{d.action}</td>
                  <td style={{ ...detailStyles.td, fontSize: '0.72rem', color: TEXT_MUTED }}>{d.fileName || '—'}</td>
                  <td style={{ ...detailStyles.td, fontFamily: 'monospace', fontSize: '0.7rem' }}>{d.ipAddress || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default ObservabilityDashboardPage;
